import React from 'react';
import { PropTypes } from 'prop-types';
import { FaInfoCircle, FaExclamation, FaCircle } from 'react-icons/fa';
import { ToastProvider } from '../../../providers';
import { withContextProvider } from '../../../hoc';
import SiteField from './SiteField';
import { formatNumericDate } from '../../../utils/dates';
import CustomFieldShow from './CustomFieldShow';
import HeaderIconTooltip from '../../HeaderIconTooltip/HeaderIconTooltip';

const SiteInfo = ({ site, values, modelFormItems }) => {
  const coordinatesText = `${site.latitude}, ${site.longitude}`;

  const tooltipContent = site.coords_set_manually
    ? 'These coordinates were manually updated by a Vamos user.'
    : 'These coordinates were automatically geocoded based on the address field. To manually update them, drag the marker on the map or update the coordinates in the site form.';

  const estimateWarning = 'Based on automated geolocating from Google, the coordinates and the address may be pointing to different locations, so be extra careful when you are navigating to this site.';

  const showEstimateWarning = site.location_type && !['ROOFTOP', 'MANUAL'].includes(site.location_type);

  const siteAttributes = {
    'Site ID': site.id,
    'Creation date': formatNumericDate(site.created_at),
    'Alternate IDs': site.alternate_ids,
    'Alternate names': site.alternate_names,
    'Site type': site.site_type,
    Units: site.units,
  };

  const workersAttributes = {
    Arrival: site.arrival ? formatNumericDate(site.arrival) : null,
    Departure: site.departure ? formatNumericDate(site.departure) : null,
    'Worker count': site.worker_count,
  };

  const locationAttributes = {
    Region: site.region,
    Coordinates: (
      <span className="items-center gap-2">
        {coordinatesText}
        <HeaderIconTooltip
          icon={<FaInfoCircle className="inline ml-1 text-vamos-blue -mt-0.5" />}
          tooltipId="coords-info"
          place="top"
          spanTextStart={tooltipContent}
          tooltipClassName="max-w-xs text-sm"
          paddingBottom="0"
          border=""
          margins="m-0"
          gap="gap-0"
          iconPosition="inline-flex"
        />
        {showEstimateWarning && (
          <HeaderIconTooltip
            icon={(
              <span className="relative inline-block w-4.25 h-4.25 ml-1">
                <FaCircle className="text-priority-medium absolute top-0 left-0 w-full h-full mt-1" />
                <FaExclamation className="text-black absolute inset-0 m-auto w-2.25 h-2.25 mt-2" />
              </span>
            )}
            tooltipId="coords-warning"
            place="top"
            spanTextStart={estimateWarning}
            tooltipClassName="max-w-xs text-sm"
            paddingBottom="0"
            border=""
            margins="m-0"
            gap="gap-0"
            iconPosition="inline-flex"
          />
        )}
      </span>
    ),
    Directions: site.directions,
  };

  return (
    <>
      <div className="max-h-200 overflow-hidden overflow-y-auto transition-[max-height] duration-150 ease-out">
        <div className="mb-12">
          <strong>Site</strong>
          <SiteField attribute={siteAttributes} />
        </div>
        <div className="mb-12">
          <strong>Workers</strong>
          <SiteField attribute={workersAttributes} />
        </div>
        <div className="mb-12">
          <strong>Location</strong>
          <SiteField attribute={locationAttributes} testid="location-cell" />
        </div>
        {/* Passing the isHeaderVisible as true to show the header */}
        <CustomFieldShow
          values={values}
          modelFormItems={modelFormItems}
          isHeaderVisible // Ensures the header is shown for SiteInfo
        />
      </div>
    </>
  );
};

SiteInfo.propTypes = {
  site: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
  modelFormItems: PropTypes.array.isRequired,
};

export default withContextProvider(ToastProvider)(SiteInfo);
